import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { AppButton } from '@easyllama/ui-components'
import { LlamaClose } from '@easyllama/ui-components/icons'
import PlansDialog from '~/organisms/Billing/PlansDialog'
import ContactSalesDialog from '~/molecules/Billing/ContactSalesDialog'
import { AppContext, useApp } from '~/context/appContext'
import { sanitizeHtml } from '~/utils/commonUtils'
import { ANNOUNCEMENT_EVENTS } from '~/service/Announcements/fetchApiClient'
import { useAnnouncementEvents } from '~/organisms/Announcements/useAnnouncementEvents'
import { useAnnouncementBilling } from '~/organisms/Announcements/useAnnouncementBilling'
import {
  ANNOUNCEMENT_BODY_ALLOWED_ATTR,
  ANNOUNCEMENT_BODY_ALLOWED_TAGS,
  ANNOUNCEMENT_BODY_SANITIZE_OPTIONS,
  UPGRADE_NOW_TYPE,
  ctaHref,
  isModifiedClick,
} from '~/organisms/Announcements/announcementBody'
import { companyBillingPath } from '~/routes'

const DEFAULT_GRADIENT_FROM = '#ff9615'
const DEFAULT_GRADIENT_TO = '#ffe4b4'

const bannerGradient = (appearance) =>
  `linear-gradient(to right, ${appearance?.gradientFrom || DEFAULT_GRADIENT_FROM}, ${appearance?.gradientTo || DEFAULT_GRADIENT_TO})`

const PageWiseBanner = ({ announcements, onVisibleChange }) => {
  const announcement = announcements?.find((candidate) => candidate.placement === 'page_banner')

  const appContext = useApp()
  const { company } = appContext
  const [contactSalesOpen, setContactSalesOpen] = useState(false)
  const [plansDialogOpen, setPlansDialogOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  const {
    headline,
    bodyHtml,
    contactCta,
    primaryCta,
    secondaryCta,
    appearance,
    mediaUrl,
    eventsUrl,
    renderToken,
    preview = false,
  } = announcement ?? {}

  const isUpgradeNowCta = primaryCta?.type === UPGRADE_NOW_TYPE
  const primaryHref = ctaHref(primaryCta?.url)
  const secondaryHref = ctaHref(secondaryCta?.url)
  const report = useAnnouncementEvents({ eventsUrl, renderToken, preview, viewOnMount: true })
  const billingContextValue = useAnnouncementBilling(appContext, { enabled: isUpgradeNowCta, renderToken })

  const isVisible = Boolean(announcement) && !dismissed

  // Lets a host page (e.g. HelpKnowledgeBaseIndex) react to the banner's own dismissed
  // state instead of guessing visibility from the announcements prop, which never changes.
  useEffect(() => {
    onVisibleChange?.(isVisible)
  }, [isVisible, onVisibleChange])

  if (!announcement) return null

  const handleContactClick = (event) => {
    if (isModifiedClick(event)) return

    event.preventDefault()
    setContactSalesOpen(true)
  }

  const handleClose = () => {
    setDismissed(true)
    report(ANNOUNCEMENT_EVENTS.CLOSED)
  }

  const handlePrimaryNavigate = () => report(ANNOUNCEMENT_EVENTS.PRIMARY_CTA)
  const handleSecondaryNavigate = () => report(ANNOUNCEMENT_EVENTS.SECONDARY_CTA)

  // primary_cta hides the banner for the session; only a conversion retires it (ENG-11753
  // Decision 6), same as the top bar's - see InAppAnnouncementBanner.
  const handleUpgradeClick = () => {
    report(ANNOUNCEMENT_EVENTS.PRIMARY_CTA).then(() => report(ANNOUNCEMENT_EVENTS.BILLING_VIEWED))
    setPlansDialogOpen(true)
  }

  const planUpgraded = () => setDismissed(true)

  const onViewDetails = () => {
    window.location.href = companyBillingPath(company?.id)
  }

  return (
    <>
      <span className='announcement-modal-root' aria-hidden='true' hidden />

      {!dismissed && (
        <div
          role='region'
          aria-label={headline || 'Page announcement'}
          className={clsx(
            'relative mb-5 flex flex-wrap items-center justify-between gap-5 w-full overflow-hidden rounded-[10px] border border-llama-grey-8 p-6 text-black',
            preview && 'pointer-events-none',
          )}
          style={{ backgroundImage: bannerGradient(appearance) }}
        >
          <button
            type='button'
            aria-label='Dismiss announcement'
            onClick={handleClose}
            className='absolute right-1.5 top-1.5 flex items-center justify-center flex-wrap rounded-full cursor-pointer text-black'
          >
            <LlamaClose className='size-5' />
          </button>

          <div className='flex flex-1 flex-col gap-3 min-w-0 sm:min-w-80'>
            <div className='[&_p]:inline [&_a]:font-medium [&_a]:underline [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ml-5 [&_ol]:ml-5'>
              {headline && <h3 className='text-xl font-bold'>{headline} </h3>}
              <div className='my-3 [&_h3]:text-lg [&_h3]:font-bold [&_h4]:text-base [&_h4]:font-bold'>
                {bodyHtml && (
                  <span
                    className='text-sm leading-relaxed'
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(bodyHtml, ANNOUNCEMENT_BODY_ALLOWED_TAGS, ANNOUNCEMENT_BODY_ALLOWED_ATTR, ANNOUNCEMENT_BODY_SANITIZE_OPTIONS),
                    }}
                  />
                )}
                {contactCta?.label && contactCta?.url && (
                  <>
                    {' '}
                    <a href={contactCta.url} onClick={handleContactClick} className='whitespace-nowrap text-sm underline'>
                      {contactCta.label}
                    </a>
                  </>
                )}
              </div>
            </div>

            <div className='flex flex-wrap items-center gap-3 pt-1'>
              {secondaryCta?.label && secondaryHref && (
                <AppButton size='sm' asChild variant='ghost' className='border border-black hover:bg-transparent max-w-full !shrink !min-w-0'>
                  <a href={secondaryHref} target='_blank' rel='noopener noreferrer' onClick={handleSecondaryNavigate}>
                    <span className='block min-w-0 truncate'>
                      {secondaryCta.label}
                    </span>
                  </a>
                </AppButton>
              )}
              {primaryCta?.label && primaryHref && (
                isUpgradeNowCta ? (
                  <AppButton size='sm' onClick={handleUpgradeClick} className='!bg-black max-w-full !shrink !min-w-0'>
                    <span className='block min-w-0 truncate'>
                      {primaryCta.label}
                    </span>
                  </AppButton>
                ) : (
                  <AppButton size='sm' asChild className='!bg-black max-w-full !shrink !min-w-0'>
                    <a href={primaryHref} target='_blank' rel='noopener noreferrer' onClick={handlePrimaryNavigate}>
                      <span className='block min-w-0 truncate'>
                        {primaryCta.label}
                      </span>
                    </a>
                  </AppButton>
                )
              )}
            </div>
          </div>

          {mediaUrl && (
            <div className='w-full max-w-[523px] lg:w-64 mr-8 shrink-0 rounded-xl'>
              <img src={mediaUrl} alt='' className='block h-auto w-full object-cover' />
            </div>
          )}
        </div>
      )}

      {isUpgradeNowCta && company && (
        <AppContext.Provider value={billingContextValue}>
          <PlansDialog
            open={plansDialogOpen}
            onOpenChange={setPlansDialogOpen}
            onSuccess={planUpgraded}
            onViewDetails={onViewDetails}
          />
        </AppContext.Provider>
      )}

      {contactSalesOpen && company && (
        <ContactSalesDialog
          open={contactSalesOpen}
          handleOpenChange={setContactSalesOpen}
        />
      )}
    </>
  )
}

export default PageWiseBanner
